import { ViewService } from '@xeetr/types/models'
import { useQuery } from '@tanstack/react-query'
import { useContext } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { queryKeys } from '../lib/queryKeys'
import { ServiceService } from '../services/Service.service'

import { useAuth } from './useAuth'

interface UseServicesReturn {
  services: ViewService[]
  isLoading: boolean
}

/**
 * Hook to access tenant services via dedicated API endpoint.
 * 
 * Phase 2: Uses dedicated /api/v3/common/management/services endpoint
 * for independent caching and optimized payload size.
 * 
 * @example
 * ```tsx
 * const { services, isLoading } = useServices()
 * services.map(service => ...)
 * ```
 */
export const useServices = (): UseServicesReturn => {
  const { tenantId } = useContext(ActiveTenantContext)
  const { getAccessTokenSilently } = useAuth()

  const { data: services = [], isLoading } = useQuery({
    queryKey: queryKeys.services.all(tenantId),
    queryFn: async () => {
      const token = await getAccessTokenSilently()
      const serviceService = new ServiceService(token, tenantId)
      return serviceService.getServices()
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes - services don't change frequently
  })

  return { services, isLoading }
}
